import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { toast } from "sonner"
import { getMetaValue } from "@/utils/meta.utils"
import { useState } from "react"

export function LoginForm({ className, ...props }: React.ComponentProps<"div">) {
  const [isEmailLoginLoading, setIsEmailLoginLoading] = useState(false)
  const [isOidcLoginLoading, setIsOidcLoginLoading] = useState(false)
  const oauthProvider = getMetaValue("openid-connect-provider") || null
  const canSignInWithPassword = getMetaValue("can-sign-in-with-password") === "true"
  const canSignInWithOpenIdConnect = getMetaValue("can-sign-in-with-openid-connect") === "true"

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isEmailLoginLoading || isOidcLoginLoading) return

    setIsEmailLoginLoading(true)
    const formData = new FormData(e.currentTarget)

    try {
      const response = await fetch("/users/sign_in", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getMetaValue("csrf-token") || ""
        },
        body: JSON.stringify({
          user: { email: formData.get("email"), password: formData.get("password") }
        }),
        credentials: "same-origin"
      })

      if (response.ok) {
        window.location.href = "/"
      } else {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error || "Unable to login, please check your credentials"
        toast.warning(errorMessage)
      }
    } catch (error) {
      toast.error("Login failed, please try again or contact support")
      console.error("Email login failed:", error)
    } finally {
      setIsEmailLoginLoading(false)
    }
  }

  const handleOpenIdConnect = async () => {
    if (isEmailLoginLoading || isOidcLoginLoading) return

    setIsOidcLoginLoading(true)

    try {
      const form = document.createElement("form")
      form.method = "POST"
      form.action = "/users/auth/openid_connect"

      const csrfToken = getMetaValue("csrf-token")
      if (csrfToken) {
        const csrfInput = document.createElement("input")
        csrfInput.type = "hidden"
        csrfInput.name = "authenticity_token"
        csrfInput.value = csrfToken
        form.appendChild(csrfInput)
      }

      document.body.appendChild(form)
      form.submit()
    } catch (error) {
      toast.error("SSO login failed, please try again or contact support")
      console.error("OpenID Connect login failed:", error)
      setIsOidcLoginLoading(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Login to your account</h1>
        <p className="text-muted-foreground text-sm text-balance">
          {canSignInWithOpenIdConnect && canSignInWithPassword && "Choose your preferred login method"}
          {!canSignInWithOpenIdConnect && canSignInWithPassword && "Enter your email below to login to your account"}
        </p>
      </div>

      <div className="grid gap-6">
        {canSignInWithOpenIdConnect && (
          <>
            <Button
              type="button"
              variant="outline"
              className="w-full border-indigo-400 text-indigo-500 hover:bg-indigo-50 hover:border-indigo-600 hover:text-indigo-800 transition-colors"
              onClick={handleOpenIdConnect}
              disabled={isEmailLoginLoading || isOidcLoginLoading}
            >
              {isOidcLoginLoading && (
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              )}
              {isOidcLoginLoading ? "Redirecting..." : `Continue with ${oauthProvider}`}
            </Button>

            {canSignInWithPassword && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>
            )}
          </>
        )}

        {canSignInWithPassword && (
          <form onSubmit={handleEmailSubmit} className="grid gap-6">
            <div className="grid gap-3">
              <Label htmlFor="email">Email</Label>
              <Input
                name="email"
                id="email"
                type="email"
                placeholder="m@example.com"
                autoComplete="email"
                required
                disabled={isEmailLoginLoading || isOidcLoginLoading}
              />
            </div>
            <div className="grid gap-3">
              <div className="flex items-center">
                <Label htmlFor="password">Password</Label>
              </div>
              <Input
                name="password"
                id="password"
                type="password"
                autoComplete="current-password"
                required
                disabled={isEmailLoginLoading || isOidcLoginLoading}
              />
            </div>
            <Button
              type="submit"
              variant="outline"
              className="w-full border-indigo-400 text-indigo-500 hover:bg-indigo-50 hover:border-indigo-600 hover:text-indigo-800 transition-colors"
              disabled={isEmailLoginLoading || isOidcLoginLoading}
            >
              {isEmailLoginLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                "Sign in with Email"
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
